export type TokenFallbackKind = 'monster' | 'npc' | 'character';

/** Marks a cached image as a locally-drawn placeholder. See isFallbackTokenImage. */
const FALLBACK_URL_PREFIX = 'data:image/svg+xml;charset=utf-8,';

const PALETTES: Record<TokenFallbackKind, Array<{ bg: string; ring: string; text: string; accent: string }>> = {
  monster: [
    { bg: '#271111', ring: '#b84a3a', text: '#f8e2c8', accent: '#67302a' },
    { bg: '#132019', ring: '#78a95e', text: '#f0f8df', accent: '#2d5135' },
    { bg: '#211626', ring: '#a66ad1', text: '#f7e8ff', accent: '#51345f' },
    { bg: '#202015', ring: '#d2a447', text: '#fff1c9', accent: '#655020' },
  ],
  npc: [
    { bg: '#161a2a', ring: '#7c6fbd', text: '#f1eeff', accent: '#3d376e' },
    { bg: '#112326', ring: '#4ca7a8', text: '#e0ffff', accent: '#24565a' },
    { bg: '#241829', ring: '#bb72a9', text: '#ffe8fa', accent: '#63365b' },
  ],
  character: [
    { bg: '#17212a', ring: '#5aa7d6', text: '#eef9ff', accent: '#2f5e78' },
    { bg: '#211d15', ring: '#d6a85a', text: '#fff3d8', accent: '#6b5128' },
    { bg: '#18241c', ring: '#6fbd7c', text: '#effff1', accent: '#355e3d' },
  ],
};

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function getInitials(name: string): string {
  const words = name
    .trim()
    .replace(/\s+\d+$/, '')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

export function createFallbackTokenImage(
  name: string,
  kind: TokenFallbackKind = 'monster',
): string {
  const safeName = name.trim() || 'Unknown';
  const palette = PALETTES[kind][hashString(`${kind}:${safeName}`) % PALETTES[kind].length];
  const initials = escapeXml(getInitials(safeName));
  const label = escapeXml(safeName.replace(/\s+\d+$/, '').slice(0, 18));

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="256" height="256">
  <defs>
    <radialGradient id="bg" cx="38%" cy="28%" r="72%">
      <stop offset="0%" stop-color="${palette.accent}"/>
      <stop offset="68%" stop-color="${palette.bg}"/>
      <stop offset="100%" stop-color="#080706"/>
    </radialGradient>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="8" stdDeviation="8" flood-color="#000000" flood-opacity="0.45"/>
    </filter>
  </defs>
  <rect width="256" height="256" fill="#0b0908"/>
  <circle cx="128" cy="128" r="112" fill="url(#bg)" filter="url(#shadow)"/>
  <circle cx="128" cy="128" r="113" fill="none" stroke="${palette.ring}" stroke-width="12"/>
  <circle cx="128" cy="128" r="94" fill="none" stroke="#fff8e6" stroke-opacity="0.18" stroke-width="2"/>
  <path d="M56 166 C84 142, 98 126, 116 74 C122 108, 146 122, 196 86 C174 124, 174 150, 204 186 C158 170, 118 176, 56 166 Z" fill="#000000" opacity="0.16"/>
  <text x="128" y="140" text-anchor="middle" font-family="Georgia, serif" font-size="74" font-weight="700" fill="${palette.text}" letter-spacing="2">${initials}</text>
  <text x="128" y="206" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="17" font-weight="700" fill="${palette.text}" opacity="0.82">${label}</text>
</svg>`.trim();

  return `${FALLBACK_URL_PREFIX}${encodeURIComponent(svg)}`;
}

/**
 * True if `url` is a locally-drawn placeholder rather than a real generated
 * image.
 *
 * Placeholders ARE cached, so a token keeps its art across a reload instead of
 * flashing back to a bare shape. But callers that decide "does this creature
 * still need generating?" must treat a placeholder as NOT resolved — otherwise
 * one transient FLUX failure caches initials forever and the creature is never
 * retried, never falls through to a local PNG, and never picks up Open5e's
 * img_main.
 */
export function isFallbackTokenImage(url: string | undefined): boolean {
  return !!url && url.startsWith(FALLBACK_URL_PREFIX);
}
